"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const logging_1 = __importDefault(require("./logging"));
const AudioWorklet_js_1 = __importDefault(require("./AudioWorklet.js"));
const AudioPortHandler_ts_1 = __importDefault(require("./AudioPortHandler.ts"));
const WorkletAudioBuffer_1 = require("./WorkletAudioBuffer");
const OutputAudioQueue_1 = require("./OutputAudioQueue");
class ScriptProcessorAudio {
    constructor(audioPort, audioContext, config, reportCallback) {
        const queue = new OutputAudioQueue_1.SimpleOutputAudioQueue(sampleRate, outputQueueMs / 1000, reportCallback);
        this.audioHandler = new AudioPortHandler_ts_1.default(audioPort, queue);
        this.audioContext = audioContext;
        this.enableInput = config.enableInput;
        this.default_buffer_size_by_sample_rate = {
            8000: 256,
            16000: 512,
            22050: 512,
            24000: 512,
            32000: 1024,
            44100: 1024,
            48000: 1024,
            88200: 2048,
            96000: 2048,
            192000: 4096,
        };
    }
    _getBufferSize(sampleRate) {
        let length = this.default_buffer_size_by_sample_rate[sampleRate];
        if (!length) {
            logging_1.default.error('webrtc', 'DvcAudio.getBufferSize', 'Sample rate not supported, attempting default buffer length');
            return 0;
        }
        return length;
    }
    _handleAudioProcess(event) {
        const audio = {};
        if (this.enableInput) {
            const inputData = event.inputBuffer.getChannelData(0);
            audio.input = [inputData];
        }
        let output = new Array(event.outputBuffer.numberOfChannels);
        for (let i = 0; i < output.length; i++) {
            output[i] = event.outputBuffer.getChannelData(i);
        }
        audio.output = output;
        this.audioHandler.process(audio);
    }
    createAudioNode() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.audioNode) {
                const buffer_size_samples = this._getBufferSize(this.audioContext.sampleRate, this.buffer_length_range_exp);
                this.audioNode = this.audioContext.createScriptProcessor(buffer_size_samples, 1, 2);
                this.audioNode.onaudioprocess = (event) => this._handleAudioProcess(event);
            }
        });
    }
    freeAudioResources() {
        if (this.audioNode) {
            this.audioNode.disconnect();
        }
        this.audioNode = null;
    }
}
class WorkletAudio {
    constructor(audioPort, audioContext, config, reportCallback) {
        this.audioPort = audioPort;
        this.audioContext = audioContext;
        this.enableInput = config.enableInput;
        this.workletModuleLoaded = this.audioContext.audioWorklet.addModule(AudioWorklet_js_1.default);
        this.config = config;
        this.reportCallback = reportCallback;
        if (config.useSharedBuffer) {
            try {
                // lengh of buffer in quanta
                const bufferLength = Math.max(3, Math.floor((audioContext.sampleRate * config.outputQueueMs) / 1000 / WorkletAudioBuffer_1.quantumSize));
                const buffer = WorkletAudioBuffer_1.createAudioBuffer(bufferLength);
                this.sharedBuffer = buffer;
            }
            catch (_a) {
                // SharedArrayBuffer may not be available; fallback to using audio channel for output
            }
        }
    }
    createAudioNode() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.audioNode) {
                yield this.workletModuleLoaded;
                if (this.audioNode) {
                    return;
                }
                this.audioNode = new window.AudioWorkletNode(this.audioContext, 'dvwc-worklet', {
                    outputChannelCount: [2],
                    processorOptions: Object.assign({ namespaces: localStorage.getItem('debug') }, this.config),
                });
                this.audioNode.port.onmessage = (event) => this.reportCallback(event.data);
                const initMessage = { audioPort: this.audioPort };
                const postInitMessage = () => this.audioNode.port.postMessage(initMessage, [this.audioPort]);
                if (this.sharedBuffer) {
                    initMessage.sharedBuffer = this.sharedBuffer;
                    try {
                        postInitMessage();
                    }
                    catch (_a) {
                        // post with SharedAudioBuffer might fail due to security considerations, retry without it
                        deleteSharedBuffer();
                        delete initMessage.sharedBuffer;
                        postInitMessage();
                    }
                }
                else {
                    postInitMessage();
                }
            }
        });
    }
    freeAudioResources() {
        this.audioNode.port.postMessage({ action: 'stop' });
        if (this.audioNode) {
            this.audioNode.disconnect();
        }
        this.audioNode = null;
    }
}
class DvcAudio {
    constructor(audioPort, audioContext, config) {
        this.audioContext = audioContext;
        const implClass = this.audioContext.audioWorklet ? WorkletAudio : ScriptProcessorAudio;
        this.impl = new implClass(audioPort, this.audioContext, config, (report) => this.collectAudioReport(report));
        this.audioReport = this.initialAudioReport();
    }
    get audioNode() {
        return this.impl.audioNode || null;
    }
    get sharedBuffer() {
        return this.impl.sharedBuffer || null;
    }
    deleteSharedBuffer() {
        delete this.impl.sharedBuffer;
    }
    createAudioNode() {
        return this.impl.createAudioNode();
    }
    startInputAudio(stream) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let mediaStreamSource = (this.mediaStreamSource = this.audioContext.createMediaStreamSource(stream));
                yield this.createAudioNode();
                this.biquadFilter = this.audioContext.createBiquadFilter();
                mediaStreamSource.connect(this.biquadFilter);
                // On some Mac computers an offset is added to the input signal causing mic clipping.
                // The issue occurs only for build-in microphone and only in several MAC models.
                // High-pass filtering is a workaround that removes the offset from original signal.
                this.biquadFilter.connect(this.audioNode);
                this.biquadFilter.type = 'highpass';
                this.biquadFilter.frequency.setValueAtTime(25, this.audioContext.currentTime);
                logging_1.default.info('webrtc', 'DvcAudio.startInputAudio', 'Input started');
            }
            catch (error) {
                logging_1.default.error('webrtc', 'DvcAudio.startInputAudio', error);
                throw error;
            }
        });
    }
    freeInputAudioResources() {
        if (this.biquadFilter) {
            this.biquadFilter.disconnect();
        }
        if (this.mediaStreamSource) {
            this.mediaStreamSource.disconnect();
        }
        this.biquadFilter = null;
        this.mediaStreamSource = null;
    }
    freeAudioResources() {
        this.impl.freeAudioResources();
        this.audioContext = null;
    }
    stopInputAudio() {
        try {
            this.freeInputAudioResources();
            logging_1.default.info('webrtc', 'DvcAudio.stopInputAudio', 'Input destroyed');
        }
        catch (error) {
            logging_1.default.error('webrtc', 'DvcAudio.stopInputAudio', error);
        }
    }
    startOutputAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.createAudioNode();
                this.audioNode.connect(this.audioContext.destination);
                logging_1.default.info('webrtc', 'DvcAudio.startOutputAudio', 'Output started');
            }
            catch (error) {
                logging_1.default.error('webrtc', 'DvcAudio.startOutputAudio', error);
                throw error;
            }
        });
    }
    stopOutputAudio() {
        try {
            this.freeAudioResources();
            logging_1.default.info('webrtc', 'DvcAudio.stopOutputAudio', 'Output destroyed');
        }
        catch (error) {
            logging_1.default.error('webrtc', 'DvcAudio.stopOutputAudio', error);
        }
    }
    /*
     * Audio report to be periodically collected to be included in telemetry report.
     * Tracks output queue/buffer underrun and overrun conditions:
     * have - is buffer in under/overrun at report generation time
     * times - number of times buffer over/underrun ended in time period
     * longestTicks - longest over/underrun in buffer push/pull ticks (audio callbacks)
     *
     * How to use these reports: Under normal circumstances we expect that under- and overruns
     * will happen due to garbage collection cycles in the worker which are longer then the
     * output queue/buffer length, typically on order of tens to hundreds of milliseconds.
     * These cycles happen every few minutes, possibly several times a minute, but we would not
     * expect them to be spaced fewer than 10 or more seconds apart. Assuming the report is
     * created e.g. every 3 seconds, we would be interested in the 'longestTicks' statistic,
     * which could be translated to milliseconds of audio using sample rate and tick length
     * corresponding to 128 samples. For mist 3-second periods we expect no under- or overruns,
     * but ocasionally we would see a single event (times = 1) and longestTicks indicating length
     * of event. Other patterns, e.g. times > 1 would indicate possible other causes which should
     * be investigated. The 'have' field may normally be ignored, but in case the worker stops
     * processing premanently or for a long period of time (order or many seconds or munutes),
     * we might never get 'times' or longestTicks', as they are generated only when the under-
     * or overrun condition ends, but we'd see 'have' set to true in many subsequent reports.
     */
    initialAudioReport() {
        return {
            outputBuffer: {
                overrun: {
                    have: false,
                    times: 0,
                    longestTicks: 0,
                },
                underrun: {
                    have: false,
                    times: 0,
                    longestTicks: 0,
                },
            },
        };
    }
    collectAudioReport(report) {
        if (report.type === 'output-buffer-condition') {
            let conditionReport;
            switch (report.condition) {
                case 'overrun':
                    conditionReport = this.audioReport.outputBuffer.overrun;
                    break;
                case 'underrun':
                    conditionReport = this.audioReport.outputBuffer.overrun;
                    break;
                default:
                    return;
            }
            switch (report.event) {
                case 'start':
                    conditionReport.have = true;
                    break;
                case 'end':
                    conditionReport.have = false;
                    conditionReport.times++;
                    if (report.count > conditionReport.longestTicks) {
                        conditionReport.longestTicks = report.count;
                    }
                    break;
                default:
                    return;
            }
        }
    }
    createAudioReport() {
        const report = Object.assign({}, this.audioReport);
        const outputBufferState = this.audioReport.outputBuffer;
        outputBufferState.overrun.times = 0;
        outputBufferState.overrun.longestTicks = 0;
        outputBufferState.underrun.times = 0;
        outputBufferState.underrun.longestTicks = 0;
        return report;
    }
}
exports.default = DvcAudio;
